import { FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Tooltip } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { TPatientInfo, TPatientPersonalInfo } from './PatientPersonalInfo.types';

import API from '../../API';
import { MainModalCreate } from '../../MainModal';
import { FlashMessageType } from '../../const';
import {
  FormValuesFromFields,
  genderTable,
  InputFields,
  normalizeAndValidateDate,
  normalizeToHalfWidth,
  RawField,
  withValidation,
} from '../../forms';
import { LocalStorageUpdateDetail } from '../../types';
import { isDevMode, STORAGE_KEY_PATIENT_INFO } from '../../utils';
import AdornmentTooltip from '../AdornmentTooltip/AdornmentTooltip';
import FlashMessageCreate from '../FlashMessage';
import Input from '../Input/Input';

const PatientPersonalInfo: FC<TPatientPersonalInfo> = () => {
  const FormFields = {
    hospitalPatientId: { ...InputFields.hospitalPatientId, tabIndex: 1 },
    patientName: { ...InputFields.patientName, tabIndex: 2 },
    gender: { ...InputFields.gender, required: true, tabIndex: 3 },
    birthdate: { ...InputFields.birthdate, required: true, tabIndex: 4 },
    email: { ...InputFields.email, tabIndex: 5 },
    preferredLang: { ...InputFields.preferredLang, required: true, tabIndex: 6 },
    comment: { ...InputFields.comment, tabIndex: 7 },
  } as const satisfies Record<string, RawField>;

  type PatientFormValues = FormValuesFromFields<typeof FormFields>;

  const formRef = useRef<HTMLFormElement | null>(null);
  const [storageData, setStorageData] = useState<TPatientInfo>();
  const [isLoading, setIsLoading] = useState(false); // State for sending requests
  const [editMode, setEditMode] = useState(false);
  const methods = useForm<PatientFormValues>({
    defaultValues: {
      hospitalPatientId: '',
      patientName: '',
      gender: 'other',
      birthdate: '',
      email: '',
      preferredLang: 'JP',
      comment: '',
    },
  });
  const {
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    clearErrors,
    control,
  } = methods;
  const onSubmit = withValidation(FormFields, methods, () => {
    setIsLoading(true);

    const formData = new FormData();
    let message = '';
    let title = '';
    let type = FlashMessageType.Success;

    for (const [key, value] of Object.entries(getValues())) {
      formData.set(key, value);
    }

    API.setPatientInfo(formData)
      .then(({ data }) => {
        message = data.msgUser;
        title = data.msgUserTitle || '';
        type = data.msgType;
        window.PatientPage.setStatus().then(() => {
          setEditMode(false);
          setIsLoading(false);
        });
      })
      .catch((err) => {
        message = err.response.data.msgUser;
        title = err.response.data.msgUserTitle || '';
        type = err.response.data.msgType;
        setIsLoading(false);
      })
      .finally(() => {
        FlashMessageCreate({ title, message, type, isSelfDestroyable: true });
      });
  });

  // Run it once just to quickly update the UI.
  useEffect(() => {
    const item = window.localStorage.getItem(STORAGE_KEY_PATIENT_INFO);
    let data: TPatientInfo = {} as TPatientInfo;
    if (item) {
      try {
        data = JSON.parse(item);
      } catch {
        window.localStorage.removeItem(STORAGE_KEY_PATIENT_INFO);
      }
    }
    setStorageData(data);
    reset({
      hospitalPatientId: data.hospitalPatientIdAuto ? '' : data.hospitalPatientId || '',
      patientName: data.patientName || '',
      gender: data.gender || 'other',
      birthdate: data.birthDate || '',
      email: data.email || '',
      comment: data.comment || '',
      preferredLang: data.preferredLang || 'JP',
    });
  }, []);

  useEffect(() => {
    const handleStorageChange = (evt: StorageEvent | CustomEvent<LocalStorageUpdateDetail<TPatientInfo>>) => {
      if (
        (evt instanceof StorageEvent && evt.key === STORAGE_KEY_PATIENT_INFO) ||
        (evt instanceof CustomEvent && evt.detail && evt.detail.key === STORAGE_KEY_PATIENT_INFO)
      ) {
        const item = window.localStorage.getItem(STORAGE_KEY_PATIENT_INFO);
        let data: TPatientInfo = {} as TPatientInfo;
        if (item) {
          try {
            data = JSON.parse(item);
          } catch {
            window.localStorage.removeItem(STORAGE_KEY_PATIENT_INFO);
          }
        }
        setStorageData(data);
        if (!editMode) {
          reset({
            hospitalPatientId: data.hospitalPatientIdAuto ? '' : data.hospitalPatientId || '',
            patientName: data.patientName || '',
            gender: data.gender || 'other',
            birthdate: data.birthDate || '',
            email: data.email || '',
            comment: data.comment || '',
            preferredLang: data.preferredLang || 'JP',
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange as EventListener);
    window.addEventListener('localstorage-update', handleStorageChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange as EventListener);
      window.removeEventListener('localstorage-update', handleStorageChange as EventListener);
    };
  }, [editMode, reset]);

  useEffect(() => {
    const onWindowLeave = (evt: BeforeUnloadEvent) => {
      evt.preventDefault();
      evt.returnValue = true;
    };

    if (isDirty) window.addEventListener('beforeunload', onWindowLeave);

    return () => {
      if (isDirty) window.removeEventListener('beforeunload', onWindowLeave);
    };
  }, [isDirty]);

  return (
    <div className='react-components-ppi'>
      {/* Header */}
      <div className='react-components-ppi__header-wrapper'>
        <h2 className='react-components-ppi__title capital-text'>受診者詳細 (受診回数:{storageData?.tagLabel})</h2>
        {editMode ? (
          <>
            <button
              onClick={() => {
                if (isDirty) {
                  MainModalCreate({
                    modalName: 'patientPersonalInfoWarningModal',
                    isDisabled: true,
                    onAccept: () => {
                      formRef.current?.requestSubmit();
                    },
                    onReject: () => {
                      setEditMode(false);
                      reset({
                        hospitalPatientId: storageData?.hospitalPatientIdAuto ? '' : storageData?.hospitalPatientId || '',
                        patientName: storageData?.patientName || '',
                        gender: storageData?.gender || 'other',
                        birthdate: storageData?.birthDate || '',
                        email: storageData?.email || '',
                        comment: storageData?.comment || '',
                        preferredLang: storageData?.preferredLang || 'JP',
                      });
                      formRef.current?.reset();
                    },
                  });
                  return;
                }
                setEditMode(false);
                reset({
                  hospitalPatientId: storageData?.hospitalPatientIdAuto ? '' : storageData?.hospitalPatientId || '',
                  patientName: storageData?.patientName || '',
                  gender: storageData?.gender || 'other',
                  birthdate: storageData?.birthDate || '',
                  email: storageData?.email || '',
                  comment: storageData?.comment || '',
                  preferredLang: storageData?.preferredLang || 'JP',
                });
              }}
              className='react-components-ppi__btn btn btn--additional'
              type='button'
              disabled={isLoading}
              tabIndex={8}
            >
              キャンセル
            </button>
            <label
              className={`react-components-ppi__btn btn btn--main ${isLoading ? 'btn--loading btn--disabled' : ''}`}
              htmlFor='react-components-ppi-form-submit-btn'
              tabIndex={9}
            >
              更新
            </label>
          </>
        ) : (
          <span onClick={() => setEditMode(true)} className='react-components-ppi__edit-btn'>
            <svg width='24' height='25' viewBox='0 0 24 25' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M14.0588 9.52L14.9788 10.44L5.91878 19.5H4.99878V18.58L14.0588 9.52ZM17.6588 3.5C17.4088 3.5 17.1488 3.6 16.9588 3.79L15.1288 5.62L18.8788 9.37L20.7088 7.54C21.0988 7.15 21.0988 6.52 20.7088 6.13L18.3688 3.79C18.1688 3.59 17.9188 3.5 17.6588 3.5ZM14.0588 6.69L2.99878 17.75V21.5H6.74878L17.8088 10.44L14.0588 6.69Z'
                fill='#009EE5'
              />
            </svg>
            編集
          </span>
        )}
        <span className='react-components-ppi__patient-id'>Brain-ID: {storageData?.patientId}</span>
      </div>
      {/* Form */}
      <form className='react-components-ppi__form-wrapper' onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <input type='submit' id='react-components-ppi-form-submit-btn' hidden />
        <Controller
          name={FormFields.hospitalPatientId.name}
          control={control}
          rules={{ required: FormFields.hospitalPatientId.required }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label={FormFields.hospitalPatientId.label}
              errorText={fieldState.error?.message}
              disabled={!editMode || isLoading}
              inputProps={{
                tabIndex: FormFields.hospitalPatientId.tabIndex,
                required: FormFields.hospitalPatientId.required,
                maxLength: FormFields.hospitalPatientId.maxLength,
                pattern: FormFields.hospitalPatientId.pattern,
              }}
              customAdornments={
                storageData?.hospitalPatientIdAuto
                  ? [
                      <AdornmentTooltip
                        key='warn'
                        type='warning'
                        title={<>自動生成されたIDです。運用上、院内で利用されているIDに変更してください。</>}
                      />,
                    ]
                  : []
              }
              type={FormFields.hospitalPatientId.type}
              size='small'
              fullWidth
              onBlur={() => {
                field.onBlur();
                normalizeToHalfWidth<PatientFormValues>(FormFields.hospitalPatientId.name, methods);
              }}
              onChange={(evt) => {
                field.onChange(evt);
                clearErrors(FormFields.hospitalPatientId.name);
              }}
            />
          )}
        />
        <Tooltip
          disableInteractive
          enterDelay={500}
          enterNextDelay={500}
          title={
            editMode && !storageData?.isEditable ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: '以下に該当するため、この情報は編集できません。<br>・結果レポートを確定しているため<br>・受診者本人が情報を入力しため',
                }}
              />
            ) : (
              ''
            )
          }
        >
          {/* HACK: Tooltip needs a DOM element to attach ref */}
          <span>
            <Controller
              name={FormFields.birthdate.name}
              control={control}
              rules={{ required: FormFields.birthdate.required }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={FormFields.birthdate.label}
                  errorText={fieldState.error?.message}
                  disabled={!storageData?.isEditable || !editMode || isLoading}
                  inputProps={{
                    tabIndex: FormFields.birthdate.tabIndex,
                    required: FormFields.birthdate.required,
                  }}
                  type={FormFields.birthdate.type}
                  size='small'
                  fullWidth
                  onBlur={() => {
                    field.onBlur();
                    normalizeAndValidateDate<PatientFormValues>(FormFields.birthdate.name, methods);
                  }}
                  onChange={(evt) => {
                    field.onChange(evt);
                    clearErrors(FormFields.birthdate.name);
                  }}
                />
              )}
            />
          </span>
        </Tooltip>
        <Controller
          name={FormFields.patientName.name}
          control={control}
          rules={{ required: FormFields.patientName.required }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label={FormFields.patientName.label}
              errorText={fieldState.error?.message}
              disabled={!editMode || isLoading}
              inputProps={{
                tabIndex: FormFields.patientName.tabIndex,
                required: FormFields.patientName.required,
                maxLength: FormFields.patientName.maxLength,
                pattern: FormFields.patientName.pattern,
              }}
              type={FormFields.patientName.type}
              size='small'
              fullWidth
            />
          )}
        />
        <Tooltip
          disableInteractive
          enterDelay={500}
          enterNextDelay={500}
          title={
            editMode && !storageData?.isEditable ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: '以下に該当するため、この情報は編集できません。<br>・結果レポートを確定しているため<br>・受診者本人が情報を入力しため',
                }}
              />
            ) : (
              ''
            )
          }
        >
          {/* HACK: Tooltip needs a DOM element to attach ref */}
          <span>
            <Controller
              name={FormFields.email.name}
              control={control}
              rules={{ required: FormFields.email.required }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={FormFields.email.label}
                  errorText={fieldState.error?.message}
                  disabled={!storageData?.isEditable || !editMode || isLoading}
                  inputProps={{
                    tabIndex: FormFields.email.tabIndex,
                    required: FormFields.email.required,
                    maxLength: FormFields.email.maxLength,
                    pattern: FormFields.email.pattern,
                  }}
                  type={FormFields.email.type}
                  size='small'
                  fullWidth
                />
              )}
            />
          </span>
        </Tooltip>
        <Tooltip
          disableInteractive
          enterDelay={500}
          enterNextDelay={500}
          title={
            editMode && !storageData?.isEditable ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: '以下に該当するため、この情報は編集できません。<br>・結果レポートを確定しているため<br>・受診者本人が情報を入力しため',
                }}
              />
            ) : (
              ''
            )
          }
        >
          <FormControl variant='outlined' size='small' error={!!errors.gender} disabled={!storageData?.isEditable || !editMode || isLoading}>
            <Controller
              name={FormFields.gender.name}
              control={control}
              rules={{ required: FormFields.gender.required }}
              render={({ field }) => (
                <RadioGroup sx={{ justifyContent: 'start' }} row {...field}>
                  <FormControlLabel
                    value='male'
                    control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                    label={genderTable.male}
                  />
                  <FormControlLabel
                    value='female'
                    control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                    label={genderTable.female}
                  />
                  <FormControlLabel
                    value='other'
                    control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                    label={genderTable.other}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
        </Tooltip>
        {window.__GlobalVars.FEATURE_LIST_LANGUAGE_SELECTOR.includes(window.__GlobalVars.hospitalCustomUrl || '') || isDevMode() ? (
          <FormControl size='small' disabled={!editMode || isLoading}>
            <InputLabel id={FormFields.preferredLang.name}>{FormFields.preferredLang.label}</InputLabel>
            <Controller
              name={FormFields.preferredLang.name}
              control={control}
              rules={{ required: FormFields.preferredLang.required }}
              render={({ field }) => (
                <Select
                  labelId={FormFields.preferredLang.name}
                  id={FormFields.preferredLang.name}
                  label={FormFields.preferredLang.label}
                  inputProps={{ tabIndex: FormFields.preferredLang.tabIndex }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                  {...field}
                >
                  <MenuItem value='JP'>日本語</MenuItem>
                  <MenuItem value='CN'>中国語（簡体字、中国）</MenuItem>
                  <MenuItem value='HK'>中国語（繁体字、香港）</MenuItem>
                  <MenuItem value='TW'>中国語（繁体字、台湾）</MenuItem>
                  <MenuItem value='EN'>英語</MenuItem>
                </Select>
              )}
            />
          </FormControl>
        ) : null}
        <Controller
          name={FormFields.comment.name}
          control={control}
          rules={{ required: FormFields.comment.required }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label={FormFields.comment.label}
              errorText={fieldState.error?.message}
              disabled={!editMode || isLoading}
              inputProps={{
                tabIndex: FormFields.comment.tabIndex,
                required: FormFields.comment.required,
                maxLength: FormFields.comment.maxLength,
                pattern: FormFields.comment.pattern,
              }}
              type={FormFields.comment.type}
              size='small'
              fullWidth
              multiline
              minRows={6}
              maxRows={6}
              sx={{ gridRow: '1/4', gridColumn: '3/4' }}
            />
          )}
        />
      </form>
    </div>
  );
};

export default PatientPersonalInfo;
